import React, { useEffect, useState } from "react"
import { FeeTypeLabel } from "../../../enums/FeeType"
import { FeeLocationLabel } from "../../../enums/FeeLocation"
import { FeePositionLabel } from "../../../enums/FeePosition"
import { InspectionFeeFamilyLabel } from "../../../enums/InspectionFeeFamily"
import { useVariableFees } from "../hooks/useVariableFees"
import { useBrands } from "../../../hooks/useBrands"
import { useSiteBrands } from "../../../hooks/useSiteBrands"
import { useVariableFeeRawData } from "../hooks/useVariableFeeRawData"
import { SiteOption } from "../../../types/SiteOption"
import { BrandOption } from "../../../types/BrandOption"
import { computeSaveTarget, ModuleVOConfigScope } from "../moduleVoNavigation"
import { getDefaultFeeRows } from "../constants"
import { applyDataToRows, mergeRowsAcrossEntities, rowKey } from "../utils/variableFeeUtils"
import UnsavedChangesAlert from "./UnsavedChangesAlert"
import ScopeSelectors from "./ScopeSelectors"

declare const window: any

interface Props
{
    mode: 'edit' | 'config'
    scope: ModuleVOConfigScope
    brand?: string | null
    dealershipIds?: number[]
}

/**
 * Un tableau (famille/localisation/position/type de frais x actif/montant), pour global, site(s),
 * marque(s), ou une combinaison. Contrairement aux frais fixes, plusieurs sites et/ou marques
 * peuvent être sélectionnés en même temps (cf. ScopeSelectors) : la valeur affichée est alors soit
 * la ligne réelle si la combinaison exacte a déjà été enregistrée, soit une fusion calculée
 * (montant max, actif si l'un des deux l'est) — cf. l'effet de calcul plus bas.
 */
export default function VariableFees({ mode, scope, brand = null, dealershipIds = [] }: Props) {
    const {
        sites,
        rows,
        setRows,
        globalData,
        selectedSite,
        setSelectedSite,
        hasChanges,
        submitting,
        loading,
        saveError,
        getGlobalRow,
        updateAmount,
        toggleActive,
        save,
    } = useVariableFees()

    const allBrandsData = useBrands(sites, scope === 'brand')
    const siteBrands = useSiteBrands(scope === 'site' ? selectedSite : null)
    const { data: rawData, loading: rawDataLoading, refetch: refetchRawData } = useVariableFeeRawData()

    const [ selectedSites, setSelectedSites ] = useState<SiteOption[]>([])
    const [ selectedBrands, setSelectedBrands ] = useState<BrandOption[]>([])
    const [ amountConflictKeys, setAmountConflictKeys ] = useState<Set<string>>(new Set())
    const [ activeConflictKeys, setActiveConflictKeys ] = useState<Set<string>>(new Set())

    // Pré-remplissage en mode édition à partir des paramètres de la ligne d'origine (Overview).
    useEffect(() => {
        if (mode !== 'edit') {
            return
        }

        if (scope === 'site' && dealershipIds.length > 0) {
            const site = sites.find(s => s.value === dealershipIds[0])
            if (site) {
                setSelectedSite(site)
                setSelectedSites([ site ])
            }
        }

        if (scope === 'brand') {
            const brandOption = allBrandsData.brands.find(b => b.value === brand)
            setSelectedBrands(brandOption ? [ brandOption ] : [])
            setSelectedSites(sites.filter(s => dealershipIds.includes(s.value)))
        }
    }, [ mode, scope, brand, dealershipIds, sites, allBrandsData ])

    // En mode config + portée "site" : un seul site sélectionné, SANS marque en configuration
    // avancée, est édité en direct via le hook (fetch réel de ce site), comme avant. Dès qu'il y
    // a plusieurs sites ou une marque avancée, on laisse selectedSite à null : sinon le fetch
    // interne du hook (asynchrone) écraserait plus tard les valeurs fusionnées ci-dessous.
    useEffect(() => {
        if (mode !== 'config' || scope !== 'site') {
            return
        }
        setSelectedSite(selectedSites.length === 1 && selectedBrands.length === 0 ? selectedSites[0] : null)
    }, [ mode, scope, selectedSites, selectedBrands ])

    // Dans tous les autres cas (plusieurs sites, plusieurs marques, ou une entité + configuration
    // avancée sur l'autre dimension), on calcule les valeurs à afficher à partir de rawData
    // plutôt qu'avec la config réelle d'un seul site (qui, elle, passe par le fetch direct du
    // hook ci-dessus). Nécessaire aussi bien en config (prévisualisation avant enregistrement)
    // qu'en édition (scope 'brand' — brand seul ou site+marque — n'est jamais couvert par le
    // fetch direct du hook, qui ne sait fetcher que par site seul).
    useEffect(() => {
        if (mode !== 'config' && mode !== 'edit') {
            return
        }

        const hasSites = selectedSites.length > 0
        const hasBrands = selectedBrands.length > 0

        const skipMerge = scope === 'site'
            ? !hasSites || (selectedSites.length === 1 && !hasBrands)
            : scope === 'brand'
                ? !hasBrands
                : true

        if (skipMerge) {
            setAmountConflictKeys(new Set())
            setActiveConflictKeys(new Set())
            return
        }

        // Une combinaison site+marque peut déjà avoir ses propres lignes en base (un
        // enregistrement précédent visant exactement ce site ET cette marque à la fois) : dans ce
        // cas on les affiche telles quelles, sans conflit à signaler — la fusion site-seul +
        // marque-seule ci-dessous ne sert qu'à prévisualiser une combinaison encore jamais
        // enregistrée telle quelle.
        const exactComboData = hasSites && hasBrands
            ? rawData.filter(r =>
                r.dealership_id != null && selectedSites.some(s => s.value === r.dealership_id) &&
                r.brand != null && selectedBrands.some(b => b.value === r.brand),
            )
            : []

        if (exactComboData.length > 0) {
            setRows(applyDataToRows(applyDataToRows(getDefaultFeeRows(), globalData), exactComboData))
            setAmountConflictKeys(new Set())
            setActiveConflictKeys(new Set())
            return
        }

        // Une combinaison site+marque doit fusionner les DEUX contributions (chacune avec repli
        // sur le global si elle n'a pas cette ligne) — jamais une dimension au détriment de
        // l'autre (avant : la marque écrasait complètement le site en scope 'site', et
        // inversement en scope 'brand').
        const entitiesData = [
            ...selectedSites.map(s => rawData.filter(r => r.dealership_id === s.value && r.brand === null)),
            ...selectedBrands.map(b => rawData.filter(r => r.brand === b.value && r.dealership_id === null)),
        ]

        const merged = mergeRowsAcrossEntities(globalData, entitiesData)
        setRows(merged.rows)
        setAmountConflictKeys(merged.amountConflictKeys)
        setActiveConflictKeys(merged.activeConflictKeys)
        // selectedSite est volontairement inclus : quand il retombe à null (site retiré, ou
        // passage à plusieurs sites/marques), l'effet interne du hook réécrit rows avec la
        // config globale seule sur le rendu suivant ; cette dépendance force cet effet-ci à
        // repasser derrière et à réappliquer la fusion correcte.
    }, [ mode, scope, selectedSites, selectedBrands, rawData, globalData, selectedSite ])

    useEffect(() => {
        window.$('[data-toggle="tooltip"]').tooltip('dispose')
        window.$('[data-toggle="tooltip"]').tooltip()
    })

    const saveTarget = computeSaveTarget(selectedSites, selectedBrands)
    const hasBothDimensions = selectedSites.length > 0 && selectedBrands.length > 0
    const conflictScopeLabel = hasBothDimensions
        ? 'le site et la marque sélectionnés'
        : selectedSites.length > 1 ? 'les sites sélectionnés' : 'les marques sélectionnées'
    const conflictOverwriteLabel = hasBothDimensions
        ? 'le site et la marque'
        : selectedSites.length > 1 ? 'tous les sites' : 'toutes les marques'

    // En config, sans marque/site choisi, il n'y a pas encore de cible : ce qui serait affiché
    // est la config globale, qu'on ne veut ni montrer ni pouvoir modifier depuis cette page.
    const hasSelection = scope === 'global' || (scope === 'site' ? selectedSites.length > 0 : selectedBrands.length > 0)
    const wantsTable = mode === 'edit' || hasSelection

    // On n'affiche le tableau qu'une fois tout ce dont on a besoin chargé (sites, marques si
    // nécessaires, lignes brutes, config globale/site) : sinon on voit d'abord les valeurs
    // globales (fetch initial du hook) puis elles changent quand le site/la marque arrive.
    const dataReady = sites.length > 0
        && !rawDataLoading
        && !loading
        && (scope !== 'brand' || !allBrandsData.loading)
    const showTable = wantsTable && dataReady

    // Référence "valeur globale" affichée sous chaque champ : pour un site seul (selectedSite,
    // déjà exclusif d'une marque en config avancée — cf. l'effet plus haut), ou pour une marque
    // seule (aucun site ajouté). Dès qu'un site ET une marque sont sélectionnés ensemble, on ne
    // sait plus dire laquelle des deux dimensions justifierait la référence : on la cache.
    const showGlobalReference = selectedSite !== null
        || (scope === 'brand' && selectedBrands.length === 1 && selectedSites.length === 0)

    return (
        <div className="container-fluid mb-2">
            <div className="row">
                <div className="col">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">
                            Vous pouvez configurer la valeur moyenne des frais fixes et variables pour les expertises
                        </span>
                        {showTable && (hasChanges || mode === 'config') && <UnsavedChangesAlert hasChanges={hasChanges} submitting={submitting} onSubmit={() => save(saveTarget).then(refetchRawData)}/>}
                    </div>
                    {saveError && (
                        <div className="alert alert-danger py-2 mb-2" role="alert">
                            <i className="fa fa-exclamation-triangle mr-1"></i>
                            {saveError}
                        </div>
                    )}

                    <ScopeSelectors
                        scope={scope}
                        disabled={mode === 'edit'}
                        sites={sites}
                        allBrandsData={allBrandsData}
                        siteBrands={siteBrands}
                        selectedSites={selectedSites}
                        onSitesChange={setSelectedSites}
                        selectedBrands={selectedBrands}
                        onBrandsChange={setSelectedBrands}
                    />

                    {!wantsTable && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-info-circle mr-1"></i>
                                Sélectionnez au moins {scope === 'site' ? 'un site' : 'une marque'} pour configurer les valeurs.
                            </small>
                        </p>
                    )}

                    {wantsTable && !dataReady && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                Chargement...
                            </small>
                        </p>
                    )}

                    {showTable && showGlobalReference && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-info-circle mr-1"></i>
                                Les valeurs globales sont affichées sous chaque champ à titre de référence.
                            </small>
                        </p>
                    )}

                    {showTable && <table className="table table-striped table-bordered">
                        <thead>
                        <tr>
                            <th>Actif</th>
                            <th>Famille</th>
                            <th>Localisation</th>
                            <th>Position</th>
                            <th>Type de frais</th>
                            <th>Montant moyen</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, index) => {
                            const globalRow = getGlobalRow(row)

                            return (
                                <tr key={index}>
                                    <td className="text-center">
                                            <span
                                                title={row.averageAmount === null ? "Renseignez d'abord un montant pour activer cette option" : undefined}
                                                style={row.averageAmount === null ? { display: 'inline-block', cursor: 'not-allowed' } : undefined}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={row.isActive}
                                                    onChange={(e) => toggleActive(row.family, row.location, row.position, row.feeType, e.target.checked)}
                                                    disabled={row.averageAmount === null}
                                                    style={row.averageAmount === null ? { pointerEvents: 'none' } : undefined}
                                                />
                                            </span>
                                        {activeConflictKeys.has(rowKey(row)) && (
                                            <i
                                                className="fa fa-info-circle text-warning ml-1"
                                                data-toggle="tooltip"
                                                data-trigger="hover"
                                                data-placement="top"
                                                data-html="true"
                                                title={`Valeurs différentes selon ${conflictScopeLabel} — actif retenu.`}
                                            ></i>
                                        )}
                                        {showGlobalReference && globalRow && (
                                            <div>
                                                <small className="text-muted">
                                                    {globalRow.is_active ? 'Actif (global)' : 'Inactif (global)'}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>{InspectionFeeFamilyLabel[row.family]}</td>
                                    <td>{FeeLocationLabel[row.location]}</td>
                                    <td>
                                        {row.position
                                            ? FeePositionLabel[row.position]
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                    <td>{FeeTypeLabel[row.feeType]}</td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={row.averageAmount ?? ''}
                                            onChange={(e) => updateAmount(row.family, row.location, row.position, row.feeType, e.target.value)}
                                            min={0}
                                        />
                                        {amountConflictKeys.has(rowKey(row)) && (
                                            <i
                                                className="fa fa-info-circle text-warning ml-1"
                                                data-toggle="tooltip"
                                                data-trigger="hover"
                                                data-placement="top"
                                                data-html="true"
                                                title={`Valeurs différentes selon ${conflictScopeLabel} — montant le plus élevé retenu. L'enregistrement écrasera la valeur pour ${conflictOverwriteLabel} sélectionné(e)s.`}
                                            ></i>
                                        )}
                                        {showGlobalReference && globalRow && (
                                            <small className="text-muted">
                                                {globalRow.amount != null
                                                    ? `${globalRow.amount / 100} € (global)`
                                                    : 'Non défini globalement'}
                                            </small>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                        </tbody>
                    </table>}
                </div>
            </div>
        </div>
    )
}
